import React from "react";
import { useNavigate } from "react-router-dom";
import { Phone, Mail, MapPin } from "lucide-react";
import { useTenant } from "@/contexts/TenantContext";
import { getImageUrl, isRootDomain, getBaseUrl } from "@/lib/utils";

/**
 * Tenant-aware footer used on product-journey pages so branding matches the homepage.
 * - White background, dark text
 * - Shows tenant logo when available; falls back to YC business logo
 * - Simple contact block consistent with homepage tone
 */
const TenantFooter: React.FC = () => {
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const isIncorpify = tenant?.subdomain === 'incorpify' || tenant?.subdomain === 'incorpify-ai';
  const INCORPIFY_LOGO = `${getBaseUrl().replace(/\/$/, '')}/Logos/incorpify/inc-logo.png`;
  const PRIMARY_MAIN_LOGO = "https://mborlyertmbornltlmdf.supabase.co/storage/v1/object/public/insurer-logos/YC4B.png";

  const rawLogo = isIncorpify
    ? INCORPIFY_LOGO
    : (tenant?.logoUrl || PRIMARY_MAIN_LOGO);
  const logo = (() => {
    if (!rawLogo) return rawLogo as string;
    if (/^https?:\/\//.test(rawLogo)) return rawLogo;
    const base = getBaseUrl();
    const p = rawLogo.startsWith('/') ? rawLogo : `/${rawLogo}`;
    if (base && base !== '/' && p.startsWith(`${base}/`)) return p;
    return base && base !== '/' ? `${base}${p}` : p;
  })();
  const name = tenant?.name || "Yallacompare for Business";

  // Footer logo sizes scaled down by 30% (relative to previous values)
  const isMainSite = isRootDomain();
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const hostSub = host.split('.')[0] || '';
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  const isIncorpifyAI = (hostSub === 'incorpify-ai') || /(?:^|\/)site\/incorpify-ai(?:\/|$)/.test(path);

  const footerLogoHeight = isMainSite ? '2.94rem' : (isIncorpifyAI ? '2.82rem' : '1.47rem');

  const goHome = () => {
    const m = window.location.pathname.match(/^(?:\/business)?\/site\/([^/]+)/);
    const prefix = m ? `/site/${m[1]}` : '';
    navigate(`${prefix}/`);
    window.scrollTo(0, 0);
  };

  return (
    <footer className={`bg-white text-gray-900 ${isIncorpify ? 'py-8' : 'py-12'} border-t mt-12`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center md:text-left">
          {/* Logo + blurb */}
          <div className="flex flex-col items-center md:items-start">
            <div className="mb-4 flex items-center justify-center md:justify-start">
              <button onClick={goHome} className="cursor-pointer">
                <img src={logo} alt={name} className="w-auto object-contain" style={{ height: footerLogoHeight }} />
              </button>
            </div>
            <p className="text-gray-600 md:text-left">Your trusted partner for business insurance in the UAE.</p>
          </div>

          {/* Contact */}
          <div className="flex flex-col items-center md:items-start">
            <h4 className="text-lg font-semibold mb-4 text-center md:text-left">Contact</h4>
            <div className="space-y-2 text-gray-600 flex flex-col items-center md:items-start">
              <div className="flex items-center justify-center md:justify-start"><Phone className="h-4 w-4 mr-2" /><span>+971586948688</span></div>
              <div className="flex items-center justify-center md:justify-start"><Mail className="h-4 w-4 mr-2" /><span>business@yallacompare.com</span></div>
              <div className="flex items-center justify-center md:justify-start"><MapPin className="h-4 w-4 mr-2" /><span>Dubai, UAE</span></div>
            </div>
          </div>

          {/* Products (links navigate home where product list exists) */}
          <div className="flex flex-col items-center md:items-start">
            <h4 className="text-lg font-semibold mb-4 text-center md:text-left">Products</h4>
            <ul className="space-y-2 text-gray-600 text-center md:text-left">
              {[
                ['/products/property-insurance', 'Property Insurance'],
                ['/products/liability-coverage', 'Liability Coverage'],
                ['/products/employee-benefits', 'Employee Benefits'],
                ['/products/cyber-protection', 'Cyber Protection'],
              ].map(([href, label]) => (
                <li key={href}>
                  <button onClick={() => navigate(prefixPath(href))} className="hover:text-gray-900">{label}</button>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div className="flex flex-col items-center md:items-start">
            <h4 className="text-lg font-semibold mb-4 text-center md:text-left">Company</h4>
            <ul className="space-y-2 text-gray-600 text-center md:text-left">
              {[
                ['/about-us', 'About Us'],
                ['/contact-us', 'Contact'],
                ['/privacy-policy', 'Privacy Policy'],
                ['/terms-and-conditions', 'Terms and Conditions'],
              ].map(([href, label]) => (
                <li key={href}>
                  <button onClick={() => navigate(prefixPath(href))} className="hover:text-gray-900">{label}</button>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="border-t border-gray-200 mt-8 pt-8 text-center text-gray-600">
          <p>&copy; 2026 Yalla Compare. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

function prefixPath(path: string): string {
  const m = window.location.pathname.match(/^(?:\/business)?\/site\/([^/]+)/);
  const prefix = m ? `/site/${m[1]}` : '';
  return `${prefix}${path}`;
}

export default TenantFooter;

